import React from 'react';
import { StyledDropdown } from 'baseui/select';
import {
	dropdownStatusStyle,
	valueContainerPaddingStyles,
	valueContainerInputBadge,
	optionContentStyle,
	inputSizesStyle,
	inputPresetMultiStyle,
	rootStyle,
	popOverBodyStyle,
	multiSumaryDropdown,
	tagOverrideDropdown,
} from './styles';
import { MIcon } from '../Icon';
import { getHTMLAttributes } from '../../Utilities/utils/Funtions/data-aria-attrs';
import { marginSize, paddingSize, CloseIconComponent } from '../../Utilities/utils/Theming';
import { getBaseCustomProps, getFormCustomProps } from '../../Utilities/types/SharedProps';
import * as meta from '../../meta-data.json';

const getOverrideDropdownElement = (props: any) => {
	return React.forwardRef<any, any>((p, ref) => {
		const { dropdownTopElement = null, dropdownBottomElement = null } = props;
		return (
			<>
				{dropdownTopElement}
				<StyledDropdown {...p} ref={ref} />
				{dropdownBottomElement}
			</>
		);
	});
};

export const overrides = (
	props: any,
	isExpanded: boolean,
	positive: boolean,
	icon: string,
	css: any,
	ref: any,
	selectRef: any,
) => {
	const HTMLAttrs = getHTMLAttributes(props);

	return {
		Root: {
			style: ({ $theme }: any) => {
				return { ...rootStyle() };
			},
			props: {
				className: props.className,
				...getBaseCustomProps(props),
				...HTMLAttrs.dataProps,
				'data-epo': 'epo-dropdown-root',
			},
		},
		/* BORDES AND COLORS */
		ControlContainer: {
			style: ({ $isFocused, $error, $multi, $theme, $disabled }: any) => {
				return {
					...(dropdownStatusStyle($error, positive, $isFocused, $disabled, $theme, props.readOnly) as any),
					...props.ControlContainerStyle,
					cursor: 'pointer',
				};
			},
			props: { 'data-epo': `epo-dropdown-${meta.version}`, part: `epo-dropdown-${meta.version}` },
		},
		/* SPACING */
		SingleValue: {
			style: () => {
				return { ...inputSizesStyle(props) };
			},
			props: {
				'data-epo': 'epo-dropdown-single-value',
			},
		},
		Input: {
			style: () => {
				return {
					...inputPresetMultiStyle(props),
					...inputSizesStyle(props),
				};
			},
			props: {
				...getFormCustomProps(props),
				...props.InputProps,
				...HTMLAttrs.ariaProps,
				autoComplete: 'off',
				readOnly: props.multi && props.searchable ? true : undefined,
				tabIndex: props.tabIndex || 0,
				'data-epo': 'epo-dropdown-input',
			},
		},
		Placeholder: {
			style: () => {
				const display = props.preset === 'multi' ? { display: 'none' } : {};
				return {
					...display,
					...inputSizesStyle(props),
					color: 'var(--epods--color-text-secondary)',
				};
			},
			props: {
				'data-epo': 'epo-dropdown-placeholder',
			},
		},
		ValueContainer: {
			style: ({ $theme }: any) => {
				return {
					cursor: props.preset === 'multi' ? 'pointer' : undefined,
					...(valueContainerInputBadge(props) as any),
					...(valueContainerPaddingStyles($theme, props) as any), // left paddings
					color: 'var(--epods--color-text-primary)',
				};
			},
			props: {
				'data-epo': 'epo-dropdown-value-container',
				onClick: (e: any) => {
					if (e.detail === 2) {
						e.preventDefault();
						e.stopPropagation();
					}
					if (props.preset === 'multi') {
						e.preventDefault();
						e.stopPropagation();
						e.target.closest('[part="epo-dropdown-30x"]').click();
						return false;
					}
				},
			},
		},
		/* CHEVRON */
		SelectArrow: {
			component: () => (
				<span
					data-epo="epo-dropdown-select-arrow"
					className={css({
						pointerEvents: 'none',
						display: 'block',
						height: '18px',
						lineHeight: 'var(--epods--font-size-p)',
						marginLeft: 'var(--epods--dropdown-action-icon-spacing)',
					})}
				>
					<MIcon
						icon={isExpanded ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
						color={
							props.disabled ? 'var(--epods--color-text-disabled)' : 'var(--epods--color-text-primary)'
						}
						size="small"
					/>{' '}
					{/* grey 950 */}
				</span>
			),
		},
		// Clear icon
		ClearIcon: {
			component: ({
				$isFocused,
				$isFocusVisible,
				$disabled,
				$size,
				$error,
				$positive,
				$required,
				$adjoined,
				...args
			}: any) => {
				const onKeyPress = (e: any) => {
					e.target?.click();
					selectRef.current.focus();
				};

				return (
					<CloseIconComponent
						style={{
							display: 'block',
							height: '18px',
							lineHeight: 'var(--epods--font-size-p)',
							marginLeft: 'var(--epods--dropdown-action-icon-spacing)',
						}}
						onKeyPress={onKeyPress}
						placement="left"
						{...args}
						// aria-label={undefined}
						tooltipContent="Clear"
						aria-label="Clear"
					/>
				);
			},
		},
		IconsContainer: {
			style: () => {
				return {
					paddingRight: props.iconAfter ? '0' : '12px', // Trick to hide the original icon
				};
			},
			props: {
				'data-epo': 'epo-dropdown-icons-container',
			},
		},
		/* ICON AND SEARCH ICON */
		SearchIcon: {
			component: () => {
				const _iconSize =
					props.size === 'large'
						? 'var(--epods--dropdown-large-icon-size)'
						: 'var(--epods--dropdown-default-icon-size)';
				return (
					<MIcon
						icon={icon}
						size={_iconSize}
						color={'var(--epods--color-text-primary)'}
						data-epo="epo-dropdown-search-icon"
					/>
				);
			},
		},
		/* DROPDOWN */

		Popover: {
			props: {
				...props.PopoverProps,
				mountNode: ref.current,
				'data-epo': 'epo-dropdown-popover',
				overrides: {
					Body: {
						style: () => ({
							...popOverBodyStyle(),
						}),
						props: {
							...props.PopoverBodyProps,
							'data-epo': `epo-dropdown-${meta.version}`,
							part: `epo-dropdown-${meta.version}`,
						},
						/* component: (bodyProps: any) => {///TODO: test after v3.0
                            return (<div style={{
                                ...popOverBodyStyle(),
                            }} {...{
                                ...props.PopoverBodyProps,
                                'data-epo': `epo-dropdown-${meta.version}`,
                                part: `epo-dropdown-${meta.version}`,
                            }}>
                                <div>{props.beforeList}asdf asdf</div>
                                <div {...bodyProps}></div>
                                <div>{props.afterList}asdf asdf </div>
                            </div>)
                        } */
					},
				},
			},
		},
		OptionContent: {
			style: ({ $selected, $isHighlighted, $theme }: any) => {
				return { ...(optionContentStyle($theme, $selected, $isHighlighted, props) as any) };
			},
			props: {
				'data-epo': 'epo-dropdown-option-content',
			},
		},
		DropdownListItem: {
			style: () => {
				return {
					...marginSize('0', '0', '0', '0'),
					...paddingSize('0', '0', '0', '0'),
					fontSize: 'var(--epods--font-size-p)',
					backgroundColor: 'var(--epods--color-bg-third-elevation)',
				};
			},
		},
		Dropdown: {
			style: () => {
				return {
					...paddingSize('0', '0', '0', '0'),
					boxShadow: 'none',
					':focus': { outline: '0' },
					...props.DropdownStyle,
				};
			},
			...((props.dropdownTopElement || props.dropdownBottomElement) && {
				component: getOverrideDropdownElement(props),
			}),
		},
		DropdownContainer: {
			style: () => {
				return {
					boxShadow: 'none',
					backgroundColor: 'var(--epods--color-bg-third-elevation)',
					...props.DropdownContainerStyle,
				};
			},
			props: { 'data-epo': `epo-dropdown-${meta.version}`, part: `epo-dropdown-${meta.version}` },
		},
		/* TAGS OVERRIDES */
		MultiValue: {
			...tagOverrideDropdown(props),
		},
		/* CONFIGURATION FOR THE MULTI WITH BAGDE: FROM PRESETS */
		InputContainer: {
			...multiSumaryDropdown(props, css),
			// ...{ TODO: check with Marcus
			//  props: props.searchable
			//      ? {}
			//      : {
			//              ...getFormCustomProps(props),
			//              ...props.InputProps,
			//              ...HTMLAttrs.dataProps,
			//              // TODO check :: readonly: props.multi && props.searchable ? true : undefined,
			//              tabIndex: props.tabIndex || 0,
			//        },
			// },
		},
		/* INPUT AND VALUES */
		LoadingIndicator: {},
	};
};
